import React, { useCallback, SyntheticEvent, useEffect, useState } from 'react';

import { Button } from 'ui-components/button/button';
import styles from './share-button.module.scss';
import { OrderManagementRequest } from 'core/entities';
import { RecordType } from 'features/offline-reports/order-management/components/requests-table/requests-table';
import { Tooltip } from 'ui-components/tooltip/tooltip';
import classNames from 'classnames';
import { UsersShareAccountIcon } from 'components/assets/Icons/UsersShareAccountIcon';
import { UsersShareIcon } from 'components/assets/Icons/UsersShareIcon';
import { useSelector } from 'react-redux';
import { selectOfflineReportsUserAccountInfo } from 'store/offline-reports/selectors/offline-reports-selectors-factory';

type UserAccounts = {
    [key: string]: string;
};
type ShareButtonProps = {
    onClick: (e: SyntheticEvent<EventTarget>) => void;
    record: RecordType | OrderManagementRequest;
    hoveringRecordId?: number;
};

export const ShareButton = ({ onClick, record, hoveringRecordId }: ShareButtonProps) => {
    const { sharedWith, target, id } = record;
    const userAccounts = useSelector(selectOfflineReportsUserAccountInfo);
    const getUsersMap = useCallback(() => {
        const result: UserAccounts = {};

        userAccounts?.members.map((member) => (result[member.id] = member.username));
        return result;
    }, [userAccounts]);
    const usersMap = getUsersMap();
    const [isHovering, setIsHovering] = useState<boolean>(false);

    useEffect(() => {
        !isHovering && hoveringRecordId === id && setIsHovering(true);
        isHovering && hoveringRecordId !== id && setIsHovering(false);
    }, [hoveringRecordId, id, isHovering]);

    return (
        <div className={styles.wrapper}>
            {sharedWith?.length === 0 && target !== 'account' && (
                <span
                    className={classNames(styles.downloadSampleButton, styles.text, {
                        [styles.isShowButton]: isHovering,
                    })}
                    data-testid="share-options-button"
                    onClick={onClick}
                >
                    Share
                </span>
            )}
            {sharedWith && sharedWith?.length > 0 && target === 'user' && (
                <Tooltip
                    placement="top"
                    title={
                        <div>
                            <div className={styles.textCenter}>Shared with users:</div>
                            {sharedWith.map((sharedId) => (
                                <div key={`shared_user_${sharedId}`}>{usersMap[sharedId]}</div>
                            ))}
                        </div>
                    }
                >
                    <Button
                        className={classNames(styles.button, styles.sharedButtonUser)}
                        data-testid="share-option-user-button"
                        onClick={onClick}
                        icon={<UsersShareIcon className={styles.buttonIcon} />}
                    ></Button>
                </Tooltip>
            )}
            {target === 'account' && (
                <Tooltip
                    placement="top"
                    title={
                        <div>
                            <div className={styles.textCenter}>Shared with account:</div>
                            <div>{userAccounts?.account_name}</div>
                        </div>
                    }
                >
                    <Button
                        className={classNames(styles.button, styles.sharedButtonAccount)}
                        data-testid="share-option-account-button"
                        onClick={onClick}
                        icon={<UsersShareAccountIcon className={styles.buttonIcon} />}
                    ></Button>
                </Tooltip>
            )}
        </div>
    );
};
